import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAuth } from 'firebase/auth'
import { FileNode, useProcessedFiles } from './hooks/useProcessedFiles'
import { RootState } from '@/store/store'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { Folder, ChevronDown, ChevronUp, Search } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { kGcsOrgHiddenFolder, kHiddenFileForFolderName } from '@/constants/constants-gcs'
import { useAppSelector } from '@/store/store-hooks'
import SourceSelectDynamicFileIcon from './utils/SourceSelectDynamicFileIcon'
import {
  selectAgentConversationFormFilePaths,
  selectAgentConversationFormTemplateFilePaths,
  selectAgentConversationFormTemplateModeActive,
} from '@/agent/chat_form/store/selectors'
import { determineGcsFileStatus, FileProcessingStage } from '@/routes/dashboard/files/utils/gcs-file-metadata-logic'
import { GCSFileMetadataStatusIndicator } from '@/routes/dashboard/files/utils/gcs-file-self-assessment'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import clsx from 'clsx'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { selectActiveMatterId, selectMattersRecord, selectRenderedMatterNameMap, selectHasLegacyUnassignedMatter } from '@/matter/store/selectors'
import { sortFileNodes, filterUnassignedItems, createUnassignedFolder, createMatterFolder } from './utils/source-selection-utils'

type DispatchFunctions = {
  addFileSelection: (file: string) => void
  toggleFileSelection: (file: string) => void
  clearFilesSelection: () => void
}

type FileDriveSelectorProps = {
  conversationId: string
  open: boolean
  dispatchFunctions: DispatchFunctions
  onClose: (value: boolean) => void
  allowMultipleSelection?: boolean
  selectedFilesOverride?: string[]
  fileReadyAtStage?: FileProcessingStage
  acceptedFileTypes?: { [type: string]: string[] }
  fileTypeNotAcceptedMessage?: string
}

/**
 * Checks if a legacy Drive file's extension matches the accepted file types.
 * @param file - The file node to check
 * @param acceptedFileTypes - The accepted file types mapping (e.g., { "application/pdf": [".pdf"] })
 * @returns true if the file type is accepted, false otherwise
 */
const isFileTypeAccepted = (file: FileNode, acceptedFileTypes?: { [type: string]: string[] }): boolean => {
  if (!acceptedFileTypes) {
    // if acceptedFileTypes is not specified, all file types are accepted
    return true
  }

  const fileName = file.name.toLowerCase()
  const allAcceptedExtensions = Object.values(acceptedFileTypes).flat()
  return allAcceptedExtensions.some((ext) => fileName.endsWith(ext.toLowerCase()))
}

/**
 * SourceSelectionDialog is a component for selecting files and folders from a user's Google Cloud Storage drive.
 * It's designed to be performant by using a flat data structure and lazy-loading folder contents.
 *
 * @component
 * @param {FileDriveSelectorProps} props - The properties for the component.
 * @param {boolean} props.open - Determines if the dialog is open.
 * @param {string} props.conversationId - The ID of the conversation.
 * @param {Object} props.dispatchFunctions - The dispatch functions for file selection.
 * @param {Function} props.dispatchFunctions.addFileSelection - Function to add a file selection.
 * @param {Function} props.dispatchFunctions.toggleFileSelection - Function to toggle a file selection.
 * @param {Function} props.dispatchFunctions.clearFilesSelection - Function to clear all file selections.
 * @param {Function} props.onClose - Function to handle closing the dialog.
 * @param {boolean} [props.allowMultipleSelection=true] - Determines if multiple file selection is allowed.
 * @param {string[]} [props.selectedFilesOverride] - Allow overriding of selected files to be provided.
 * @param {FileProcessingStage} [props.fileReadyAtStage] - What stage of file processing files must reach to be ready (defaults to EMBEDDINGS_INDEXED).
 * @param {Object} [props.acceptedFileTypes] - The file types selectable through the dialog. Files of other types will be greyed out and not selectable.
 *
 * @returns {JSX.Element | null} The rendered component or null if not open.
 *
 * @example
 * <SourceSelectionDialog
 *   open={true}
 *   conversationId="123"
 *   dispatchFunctions={{
 *     addFileSelection: (id) => console.log(id),
 *     toggleFileSelection: (id) => console.log(id),
 *     clearFilesSelection: () => console.log('clear')
 *   }}
 *   onClose={() => console.log('close')}
 * />
 */
export const SourceSelectionDialog: React.FC<FileDriveSelectorProps> = ({
  open,
  conversationId,
  dispatchFunctions: { addFileSelection, toggleFileSelection, clearFilesSelection },
  onClose,
  allowMultipleSelection = true,
  selectedFilesOverride,
  fileReadyAtStage,
  acceptedFileTypes,
  fileTypeNotAcceptedMessage,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFolders, setExpandedFolders] = useState<Array<string>>([])
  const [addingFiles, setAddingFiles] = useState<boolean>(false)
  const [isDialogLoading, setIsDialogLoading] = useState<boolean>(true)

  const auth = getAuth()
  const uid = auth.currentUser?.uid

  // --- Redux State and Derived Logic ---
  const isTemplateModeActive = useAppSelector(selectAgentConversationFormTemplateModeActive)
  const selectedFilePaths = useAppSelector((state: RootState) => selectAgentConversationFormFilePaths(state, { conversationId: conversationId }))
  const templateFiles = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId: conversationId }))

  // Get the matter id - try conversation first, then fall back to active matter
  // They should always be the same, since when we are in a conversation, we are in a matter.
  const agentConversationMatterId = useAppSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))
  const activeMatterId = useAppSelector(selectActiveMatterId)

  // Priority: conversation matter > active matter (for Med Chron/Med Bill)
  const matterId = agentConversationMatterId ?? activeMatterId
  const matterFolderPath = matterId && uid ? `users/${uid}/${matterId}` : null

  // Get matter names map for display
  const matterNameMap = useAppSelector(selectRenderedMatterNameMap)
  const matterDisplayName = matterId ? matterNameMap.get(matterId) ?? 'Matter' : 'Matter'

  // Get all matter IDs to filter them out of Unassigned folder
  const mattersRecord = useAppSelector(selectMattersRecord)
  const allMatterIds = useMemo(() => new Set(Object.keys(mattersRecord)), [mattersRecord])

  // Check if user has a LEGACY_UNASSIGNED matter
  const hasLegacyUnassignedMatter = useAppSelector(selectHasLegacyUnassignedMatter)

  const activeMatterFolderRef = useRef<HTMLDivElement>(null)

  const selectedFiles = selectedFilesOverride ?? (isTemplateModeActive ? templateFiles : selectedFilePaths)

  const driveRootPath = `users/${uid}`

  const { loading, nodes, expandFolder, refresh } = useProcessedFiles({ path: driveRootPath, fileReadyAtStage })

  // Create "Unassigned" folder that groups root-level items not in matter folders
  const unassignedFolder: FileNode = useMemo(() => createUnassignedFolder(driveRootPath), [driveRootPath])

  // Show Unassigned folder only if user has LEGACY_UNASSIGNED matter AND has items in it.
  // Guard: without LEGACY_UNASSIGNED matter, unassigned files cannot exist.
  const shouldShowUnassignedFolder = useMemo(() => {
    if (!hasLegacyUnassignedMatter) return false
    return filterUnassignedItems(nodes, driveRootPath, allMatterIds).length > 0
  }, [hasLegacyUnassignedMatter, nodes, driveRootPath, allMatterIds])

  // Build root folder list: Matter folder (if conversation has one) + Unassigned (if should show)
  const rootFoldersToShow = useMemo((): FileNode[] => {
    const folders: FileNode[] = []

    if (shouldShowUnassignedFolder) {
      folders.push(unassignedFolder)
    }

    if (matterFolderPath) {
      const existingNode = nodes[matterFolderPath]
      // Only use existing node if it's a valid FileNode (has required properties)
      // expandFolder can create partial nodes with only isLoading/isExpanded
      const isValidNode = existingNode?.id && existingNode?.name

      const matterFolder: FileNode = isValidNode ? existingNode : createMatterFolder(matterFolderPath, matterDisplayName, driveRootPath, existingNode, loading)
      // unshift to make sure the matter folder is at the beginning of the list
      folders.unshift(matterFolder)
    }

    return folders
  }, [shouldShowUnassignedFolder, unassignedFolder, matterFolderPath, matterDisplayName, driveRootPath, nodes, loading])

  // Get items that belong in the Unassigned folder (root items that aren't in ANY matter folder)
  const getUnassignedItems = useCallback(() => filterUnassignedItems(nodes, driveRootPath, allMatterIds), [nodes, driveRootPath, allMatterIds])

  // --- Event Handlers ---

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>, file: FileNode) => {
    const isSelected = e.target.checked
    if (isSelected) {
      addFileSelection(file.id)
    } else {
      toggleFileSelection(file.id)
    }
  }

  /**
   * Determines if a file is selectable based on the file status and accepted file types.
   * If the file is not DONE, it will return false.
   * If the file is DONE and the acceptedFileTypes parameter is specified, it will check if the file type is in the accepted file types.
   * If the file is DONE and the acceptedFileTypes parameter is not specified, it will assume all file types are accepted and return true.
   */
  const isFileSelectable = useCallback(
    (file: FileNode) => {
      const fileMetadata = file.metadata
      if (fileMetadata) {
        const fileStatus = determineGcsFileStatus({ fileMetadata, fileReadyAtStage })
        // validate the file has completed processing
        if (fileStatus.status === GCSFileMetadataStatusIndicator.DONE) {
          // if acceptedFileTypes is specified, validate the file type
          return isFileTypeAccepted(file, acceptedFileTypes)
        }
      }
      return false
    },
    [fileReadyAtStage, acceptedFileTypes]
  )

  /**
   * Handle Check All Folder Children (single layer)
   * When a folder's checkbox is clicked, this will select all of the children.
   *
   * STEPS:
   * 1. Expand the folder to load its children (returns the child files)
   * 2. Select the child files
   */
  const handleToggleCheckAllFolderChildren = async (e: React.ChangeEvent<HTMLInputElement>, folder: FileNode) => {
    const isSelected = e.target.checked
    setAddingFiles(true)

    const foldersToExpand = new Set<string>()

    const processFolderRecursively = async (folderToProcess: FileNode) => {
      // If selecting, ensure this folder is marked for visual expansion.
      if (isSelected) {
        foldersToExpand.add(folderToProcess.id)
      }

      // For unassigned folder, get root items that aren't the current matter
      // For real folders, call expandFolder to fetch children
      let children: FileNode[]
      if (folderToProcess.isVirtualFolder) {
        children = getUnassignedItems()
      } else {
        const childrenResult = await expandFolder(folderToProcess.id)
        children = childrenResult ? Object.values(childrenResult) : []
      }

      // Process all children of the current folder.
      for (const child of children) {
        if (child.isFolder) {
          await processFolderRecursively(child)
        } else {
          // File selection logic...
          if (!isFileSelectable(child)) continue
          if (child.name === kHiddenFileForFolderName) continue
          if (searchQuery !== '' && !child.name.toLowerCase().includes(searchQuery.toLowerCase())) continue

          if (isSelected) {
            addFileSelection(child.id)
          } else {
            toggleFileSelection(child.id)
          }
        }
      }
    }

    try {
      await processFolderRecursively(folder)
      // After processing, if we were selecting, update the UI to expand the folders.
      if (isSelected && foldersToExpand.size > 0) {
        setExpandedFolders((prev) => [...new Set([...prev, ...Array.from(foldersToExpand)])])
      }
    } finally {
      setAddingFiles(false)
    }
  }

  const handleFolderExpandToggle = (folderId: string) => {
    // Virtual unassigned folder - just toggle UI expansion (no GCS fetch needed)
    if (folderId === unassignedFolder.id) {
      if (expandedFolders.includes(folderId)) {
        setExpandedFolders((prev) => prev.filter((id) => id !== folderId))
      } else {
        setExpandedFolders((prev) => [...prev, folderId])
      }
      return
    }

    const folder = nodes[folderId]
    if (!folder) return

    if (expandedFolders.includes(folderId)) {
      setExpandedFolders((prev) => prev.filter((id) => id !== folderId))
    } else {
      if (!folder.isExpanded && !folder.isLoading) {
        expandFolder(folder.id)
      }
      setExpandedFolders((prev) => [...prev, folderId])
    }
  }

  const handleClearAllSelections = () => {
    setExpandedFolders([])
    setSearchQuery('')
    clearFilesSelection()
  }

  // --- State-derived Logic and Memoized Callbacks ---

  /**
   * Memoized helper to recursively check if a folder contains any selectable files.
   * This prevents re-calculating the same folder multiple times during a render.
   */
  const hasSelectableChildren = useMemo(() => {
    const memo: Record<string, boolean> = {}

    const check = (folderId: string): boolean => {
      if (memo[folderId] !== undefined) return memo[folderId]

      // For unassigned folder, get root items that aren't the current matter
      const children = folderId === unassignedFolder.id ? getUnassignedItems() : Object.values(nodes).filter((node) => node.parentId === folderId)

      if (children.length === 0) {
        memo[folderId] = false
        return false
      }

      for (const child of children) {
        if (child.name === kHiddenFileForFolderName) continue

        if (child.isFolder) {
          if (check(child.id)) {
            memo[folderId] = true
            return true
          }
        } else {
          if (isFileSelectable(child)) {
            memo[folderId] = true
            return true
          }
        }
      }

      memo[folderId] = false
      return false
    }

    return check
  }, [nodes, isFileSelectable, getUnassignedItems])

  /**
   * Determines if a folder should be displayed as "selected" (i.e., its checkbox is checked).
   * A folder is considered selected if all of its "relevant" children are selected.
   * "Relevant" children are selectable files or folders that themselves contain selectable files.
   */
  const isFolderSelected = useCallback(
    (folder: FileNode): boolean => {
      // For unassigned folder, get root items that aren't the current matter
      const children = folder.isVirtualFolder ? getUnassignedItems() : Object.values(nodes).filter((node) => node.parentId === folder.id)

      // Filter for children that are "relevant" for selection.
      const relevantChildren = children.filter((child) => {
        if (child.name === kHiddenFileForFolderName) return false

        if (child.isFolder) {
          // A subfolder is only relevant if it contains selectable files.
          return hasSelectableChildren(child.id)
        } else {
          // A file is only relevant if it's selectable.
          return isFileSelectable(child)
        }
      })

      // If there are no relevant children, the folder cannot be considered "selected".
      if (relevantChildren.length === 0) {
        return false
      }

      // Every relevant child must be selected for the folder to be considered selected.
      return relevantChildren.every((child) => {
        if (child.isFolder) {
          // If a child is a folder, it must also be fully selected (recursively).
          return isFolderSelected(child)
        } else {
          // If a child is a file, it must be in the selectedFiles list.
          return selectedFiles.includes(child.id)
        }
      })
    },
    [nodes, selectedFiles, hasSelectableChildren, isFileSelectable, getUnassignedItems]
  )

  const isFileSelected = (fileId: string) => {
    return selectedFiles.includes(fileId)
  }

  /**
   * Filters the flat `nodes` record based on the search query.
   * It returns an array of all nodes that either match the query or are a parent of a node that matches.
   * This ensures that the full path to a matched file is always visible.
   */
  const getFilteredNodes = useCallback(() => {
    const allNodes = Object.values(nodes)
    if (!searchQuery) {
      return allNodes
    }
    const lowerCaseQuery = searchQuery.toLowerCase()
    const filtered = new Set<FileNode>()
    const parents = new Set<string>()

    allNodes.forEach((node) => {
      if (node.name.toLowerCase().includes(lowerCaseQuery)) {
        filtered.add(node)
        let parentId = node.parentId
        while (parentId) {
          parents.add(parentId)
          const parentNode = nodes[parentId]
          parentId = parentNode ? parentNode.parentId : null
        }
      }
    })

    allNodes.forEach((node) => {
      if (parents.has(node.id)) {
        filtered.add(node)
      }
    })

    return Array.from(filtered)
  }, [nodes, searchQuery])

  // Reset loading state when dialog closes so it's ready for the next open
  useEffect(() => {
    if (!open) {
      setIsDialogLoading(true)
    }
  }, [open])

  /**
   * Pre-opens the parent folders of any files that are already selected when the dialog is opened.
   * It traverses up the hierarchy from each selected file to find all parent folders that need to be expanded.
   */
  useEffect(() => {
    if (!open) return

    let isMounted = true

    const openActions = async () => {
      // Expand the matter folder if present
      if (matterFolderPath) {
        await expandFolder(matterFolderPath)
        if (isMounted) {
          setExpandedFolders((prev) => [...new Set([...prev, matterFolderPath])])
        }
      }

      // Refresh and expand folders containing selected files
      const foldersToExpand = await refresh(selectedFiles)
      if (!isMounted) return

      setExpandedFolders((prev) => [...new Set([...prev, ...foldersToExpand])])
      setIsDialogLoading(false)

      // Scroll to active matter folder after DOM updates
      setTimeout(() => {
        activeMatterFolderRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 100)
    }

    openActions()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matterFolderPath])

  // --- Rendering ---
  /**
   * Recursively renders the file and folder structure from a given list of nodes.
   * It starts with a `parentId` and renders all nodes from the list that have that parentId.
   * For each folder it renders, it calls itself with the folder's ID as the new `parentId`.
   */
  const renderFiles = (nodesToRender: FileNode[], parentId: string | null, level = 0): (JSX.Element | null)[] => {
    // Check if we're rendering children of the virtual unassigned folder
    const isRenderingVirtualUnassigned = parentId === unassignedFolder.id

    const children = nodesToRender
      .filter((node) => {
        // When rendering virtual unassigned folder, show loose root items (but not the virtual folder itself)
        if (isRenderingVirtualUnassigned) {
          // Exclude the unassigned folder from its own children to prevent infinite recursion
          if (node.isVirtualFolder) return false
          // Show root-level items that aren't ANY matter folder
          if (node.parentId !== driveRootPath) return false
          // Extract folder name from path (last segment) and check if it's a matter ID
          const folderName = node.id.split('/').pop() ?? ''
          if (allMatterIds.has(folderName)) return false
          return true
        }

        // Normal case: filter by parentId
        return node.parentId === parentId
      })
      .filter((node) => {
        // At root level (level 0), show only current matter folder + unassigned folder
        if (level === 0) {
          if (node.isVirtualFolder) return true
          // Only show the current matter folder (if conversation has one)
          return node.id === matterFolderPath
        }
        return true
      })
      .sort((a, b) => sortFileNodes(a, b, level))

    if (children.length === 0) {
      // Show loading only when this folder is actively fetching its children
      // - Virtual folders (unassigned) never fetch, so parentNode is null → false
      // - Real folders: expandFolder() sets node.isLoading = true when request starts
      const parentNode = parentId ? nodes[parentId] : null
      const isParentLoading = parentNode?.isLoading === true

      if (isParentLoading) {
        // Show a loading spinner inside the folder
        return [
          <div key={`loading-${parentId}`} style={{ paddingLeft: `${level * 20}px` }} className={'py-2 flex items-center'}>
            <CircularProgressContinuousSized size={16} thickness={7} />
            <span className="ml-2 text-brand-neutral-500">Loading...</span>
          </div>,
        ]
      }

      return [
        <div key={`empty-${parentId}`} style={{ paddingLeft: `${(level - 1) * 20 + 36}px` }} className={'pb-2 text-brand-neutral-500'}>
          This folder is empty.
        </div>,
      ]
    }

    return children.map((file) => {
      // Hide system files: placeholder files for empty folders, and org hidden folder at root/unassigned level
      const isOrgHiddenFolder = file.name === kGcsOrgHiddenFolder && (level === 0 || isRenderingVirtualUnassigned)
      if (file.name === kHiddenFileForFolderName || isOrgHiddenFolder) {
        return null
      }

      const fileIsSelectable = isFileSelectable(file)
      const tooltipMessage =
        !isFileTypeAccepted(file, acceptedFileTypes) && fileTypeNotAcceptedMessage ? (
          <span className="text-sm px-1 py-2 font-normal font-sans">{fileTypeNotAcceptedMessage}</span>
        ) : (
          ''
        )
      const isExpanded = expandedFolders.includes(file.id)

      return (
        <Combobox.Option key={file.id} value={file} as={Fragment}>
          {file.isFolder ? (
            <>
              <div
                ref={file.id === matterFolderPath ? activeMatterFolderRef : null}
                style={{ paddingLeft: `${level * 20}px` }}
                className={`cursor-default select-none py-2 items-center border-t-[1px] text-wrap grid hover:bg-brand-neutral-100 ${
                  allowMultipleSelection ? 'grid-cols-[32px_auto]' : ''
                }`}
              >
                {allowMultipleSelection && (
                  <input
                    id={`file-checkbox-${file.id}_a`}
                    type="checkbox"
                    checked={isFolderSelected(file)}
                    onChange={(e) => handleToggleCheckAllFolderChildren(e, file)}
                    className={`ml-2 h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 disabled:bg-brand-neutral-100 disabled:cursor-not-allowed ${
                      addingFiles && 'cursor-wait'
                    }`}
                    disabled={file.isLoading}
                  />
                )}
                <div
                  onClick={() => handleFolderExpandToggle(file.id)}
                  className={'pl-1 pr-2 grid grid-cols-[28px_auto_20px] items-center text-sm cursor-pointer text-wrap'}
                >
                  <Folder className="w-5 h-5" />
                  <span className={'compatible-word-break pr-2'}>{file.displayName}</span>
                  {file.isLoading ? <CircularProgressContinuousSized size={16} thickness={7} /> : isExpanded ? <ChevronUp /> : <ChevronDown />}
                </div>
              </div>
              {isExpanded && renderFiles(nodesToRender, file.id, level + 1)}
            </>
          ) : (
            <CustomizableTooltip title={tooltipMessage} placement="top-start" arrow offset={[-20, -15]} arrowXOffset={-272}>
              <div
                style={{ paddingLeft: `${level * 20}px` }}
                className={`cursor-default select-none py-2 items-center border-t-[1px] grid grid-cols-[32px_auto] hover:bg-brand-neutral-100`}
              >
                <input
                  id={`file-checkbox-${file.id}`}
                  type="checkbox"
                  checked={isFileSelected(file.id)}
                  onChange={(e) => handleFileSelection(e, file)}
                  className="ml-2 h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 disabled:bg-brand-neutral-100 disabled:cursor-not-allowed"
                  disabled={!fileIsSelectable}
                />
                <label
                  htmlFor={`file-checkbox-${file.id}`}
                  className={clsx(
                    'pl-1 pr-2 items-center grid grid-cols-[30px_auto]',
                    fileIsSelectable && 'cursor-pointer',
                    !fileIsSelectable && 'text-brand-neutral-400 cursor-not-allowed'
                  )}
                >
                  <SourceSelectDynamicFileIcon fileMetadata={file.metadata} fileReadyAtStage={fileReadyAtStage} />
                  <div className={clsx('compatible-word-break', !fileIsSelectable && 'text-brand-neutral-500')}>{file.name}</div>
                </label>
              </div>
            </CustomizableTooltip>
          )}
        </Combobox.Option>
      )
    })
  }

  if (!open) return null

  const filteredNodes = getFilteredNodes()
  // Combine fetched nodes with our root folders (matter + unassigned)
  // Exclude matter folder from filteredNodes since it's already in rootFoldersToShow
  const filesToRender = [...rootFoldersToShow, ...filteredNodes.filter((node) => node.id !== matterFolderPath)]

  return (
    <Transition.Root show={open} as={Fragment} appear>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto px-4 py-20 lg:ml-[300px]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl transform rounded-xl bg-white p-2 shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
              <div className="px-4 pt-4 pb-3 justify-start text-brand-neutral-950 text-xl font-semibold font-rethink-sans">Add from Matter Files</div>
              <Combobox>
                <div className="relative mb-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-neutral-500" aria-hidden="true" />
                  <Combobox.Input
                    className="w-full rounded-md border-0 bg-gray-100 pl-10 pr-4 py-2.5 text-gray-900 focus:ring-0 sm:text-sm"
                    value={searchQuery}
                    placeholder="Search opened folders..."
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  />
                </div>
                <div className={'overflow-y-scroll'}>
                  <Combobox.Options static className="max-h-96 sm:max-h-[600px] py-2 text-sm text-gray-800">
                    {isDialogLoading ? (
                      <div className="flex justify-center m-4">
                        <CircularProgressContinuousSized size={30} thickness={5} />
                      </div>
                    ) : (
                      renderFiles(filesToRender, driveRootPath)
                    )}
                  </Combobox.Options>
                </div>
                <div className={'flex gap-x-4 pt-2'}>
                  <button
                    type={'button'}
                    onClick={() => handleClearAllSelections()}
                    className={
                      'flex-auto items-center rounded-md bg-white border-[1px]  px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  ' +
                      (selectedFiles.length === 0
                        ? 'border-true-gray-300 text-true-gray-300 cursor-not-allowed'
                        : 'border-brand-500 text-brand-500 hover:bg-brand-300 focus-visible:outline-brand-500')
                    }
                    disabled={selectedFiles.length === 0}
                  >
                    Clear
                  </button>
                  <button
                    type={'button'}
                    onClick={() => onClose(false)}
                    className={
                      'flex-auto items-center rounded-md bg-brand-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500'
                    }
                  >
                    Done
                  </button>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
